import { api, API_BASE_URL } from './api'
import { USE_MOCK_DATA } from './dashboardService'

/** Filter keys understood by GET /admin/crm (also used as URL query params). */
export const CRM_FILTER_KEYS = [
    'search', 'recordType', 'accountStatus', 'trialStatus', 'incompleteSettings',
    'noMatches', 'neverLoggedIn', 'suspended', 'reactivated', 'activeInPeriod',
    'receivingProfileMatches', 'receivingWiderDiscovery', 'endingWithin7Days', 'period',
    'settingsComplete', 'newPaying', 'openedOpportunities', 'savedOpportunities', 'viewedDocuments', 'accountType',
]

export function buildQuery(params = {}) {
    const q = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') q.set(k, v)
    })
    const s = q.toString()
    return s ? `?${s}` : ''
}

const MOCK_ROWS = [
    { id: 'o1', recordType: 'organisation', name: 'Acme Care Ltd', contact: 'Jo Bloggs', email: 'jo@acmecare.example', accountStatus: 'active_paid', trialStatus: null, lastLogin: '2026-09-28T09:12:00Z', incompleteSettings: false, noMatches: false },
    { id: 'o2', recordType: 'organisation', name: 'Beacon Services', contact: 'Sam Lee', email: 'sam@beacon.example', accountStatus: 'active_trial', trialStatus: 'active', lastLogin: null, incompleteSettings: true, noMatches: true },
    { id: 'u1', recordType: 'user', name: 'Jo Bloggs', contact: 'Acme Care Ltd', email: 'jo@acmecare.example', accountStatus: 'active', trialStatus: null, lastLogin: '2026-09-28T09:12:00Z' },
    { id: 'i1', recordType: 'insights_contact', name: 'Pat Morgan', contact: 'Morgan Health', email: 'pat@morgan.example', accountStatus: null, trialStatus: null, lastLogin: null, source: 'Insights newsletter', consent: 'Marketing opt-in 2026-03-02' },
]

const MOCK_DETAIL = {
    id: 'o1', name: 'Acme Care Ltd', primaryContact: { name: 'Jo Bloggs', email: 'jo@acmecare.example' },
    registeredAt: '2026-01-14T10:00:00Z', accountStatus: 'active_paid',
    subscriptionHistory: [
        { date: '2026-01-14', event: 'Trial started' },
        { date: '2026-01-28', event: 'Trial converted to paid (monthly)' },
    ],
    lifecycle: { suspended: false, reactivated: false, deletionRequested: false },
    lastLogin: '2026-09-28T09:12:00Z', usage: { opened: 42, documentsViewed: 9 },
    tenderSettings: { complete: true, services: ['Domiciliary care'], regions: ['North West'] },
    counts: { profileMatches: 14, widerDiscovery: 31, saved: 6 },
    users: [{ id: 'u1', name: 'Jo Bloggs', email: 'jo@acmecare.example', role: 'Owner', status: 'active' }],
    linkedContacts: [],
}

/** Backend CRM list rows (snake_case) → the shape the CRM page renders. */
function mapListRow(r) {
    return {
        id: r.id,
        recordType: r.record_type,
        name: r.name,
        contact: r.record_type === 'organisation' ? null : r.organisation_name,
        email: r.email,
        accountStatus: r.account_status,
        trialEnd: r.trial_end,
        lastLogin: r.last_login_at,
        source: r.source,
        isInternal: !!r.is_internal,
        organisationId: r.organisation_id,
        createdAt: r.created_at,
    }
}

/** Backend organisation detail ({ organisation, users, … }) → the flat shape the detail page renders. */
function mapDetail(d) {
    const o = d.organisation || {}
    const ts = d.tenderSettings
    return {
        id: o.id,
        name: o.name,
        isInternal: !!o.isInternal,
        primaryContact: d.primaryContact,
        registeredAt: o.registeredAt,
        accountStatus: o.accountStatus,
        lifecycle: {
            suspended: o.accountStatus === 'suspended',
            reactivated: !!o.reactivatedAt,
            deletionRequested: !!o.deletionRequestedAt,
        },
        lastLogin: d.usage?.lastLoginAt || null,
        usage: {},
        counts: d.counts,
        tenderSettings: ts
            ? { complete: (ts.serviceTypes || []).length > 0, services: ts.serviceTypes, regions: ts.regions, commissioners: ts.commissioningAuthorities }
            : { complete: false },
        subscriptionHistory: (d.subscriptionHistory || []).map((h) => ({ date: h.created_at, event: String(h.event_type || '').replace(/_/g, ' ') })),
        users: d.users || [],
        linkedContacts: d.insightsContacts || [],
    }
}

const delay = (v) => new Promise((r) => setTimeout(() => r(v), 150))

export const crmService = {
    async list(params = {}) {
        if (USE_MOCK_DATA) {
            let rows = MOCK_ROWS
            if (params.recordType) rows = rows.filter((r) => r.recordType === params.recordType)
            if (params.search) {
                const s = params.search.toLowerCase()
                rows = rows.filter((r) => `${r.name} ${r.contact} ${r.email}`.toLowerCase().includes(s))
            }
            return delay({ success: true, data: { rows, total: rows.length, page: 1, pageSize: Number(params.pageSize) || 25 } })
        }
        const res = await api.get(`/admin/crm${buildQuery(params)}`)
        if (res?.data?.rows) res.data.rows = res.data.rows.map(mapListRow)
        return res
    },

    async getOrganisation(id) {
        if (USE_MOCK_DATA) return delay({ success: true, data: { ...MOCK_DETAIL, id } })
        const res = await api.get(`/admin/crm/organisations/${id}`)
        if (res?.data?.organisation) res.data = mapDetail(res.data)
        return res
    },

    /** Downloads the export for the current filters. format: 'csv' | 'xlsx' */
    async exportFile(params = {}, format = 'csv') {
        if (USE_MOCK_DATA) {
            const header = 'recordType,name,contact,email\n'
            const body = MOCK_ROWS.map((r) => [r.recordType, r.name, r.contact, r.email].join(',')).join('\n')
            return triggerDownload(new Blob([header + body], { type: 'text/csv' }), 'crm-export-mock.csv')
        }
        const token = localStorage.getItem('admin_token')
        const res = await fetch(`${API_BASE_URL}/admin/crm/export${buildQuery({ ...params, format })}`, {
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!res.ok) {
            const err = await res.json().catch(() => ({ message: 'Export failed' }))
            throw new Error(err.message || `HTTP ${res.status}`)
        }
        triggerDownload(await res.blob(), `crm-export.${format}`)
    },

    /** Flag / unflag an organisation as an internal or test account (excluded from customer metrics). */
    async setInternal(id, isInternal) {
        if (USE_MOCK_DATA) return delay({ success: true, data: { id, isInternal } })
        return api.patch(`/admin/crm/organisations/${id}/internal`, { isInternal })
    },

    /** Explicit, confirmed link/merge. Never called without confirm:true. */
    async link({ insightsContactId, organisationId }) {
        if (USE_MOCK_DATA) return delay({ success: true })
        return api.post('/admin/crm/link', { insightsContactId, organisationId, confirm: true })
    },
}

function triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

export default crmService
